import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import { API_BASE_URL } from '../constants/system'
import { skyProductURL, skyQueryKey } from './socialdash/skyEndpoints'
import type { EntityFilterSummary, EntityMetricBounds, LabelValue } from '../types/gotypes'

export type { EntityFilterSummary, EntityMetricBounds, LabelValue }

interface UseEntityFilterSummaryOptions {
  customer: string
  job: string
  enabled?: boolean
  /**
   * A Sky product slug. When given, the request goes through `skyProductURL`
   * instead of `/autocoder/:customer/:job/…`, and the response is scoped to that
   * product's campaigns.
   *
   * 🔴 THIS IS WHY THE OPTION EXISTS RATHER THAN A HAND-ROLLED PATH.
   * `skyEndpoints.ts` names the hazard exactly: a hook that builds its own
   * `/autocoder/${customer}/${job}/…` path "would still WORK while silently
   * bypassing skyProductScope" — a 200 with another department's numbers under
   * this department's heading. Both facet hooks used to do precisely that, so
   * on a product dashboard they were served job-wide facets and looked fine.
   *
   * Omitted means the job-wide mount, which is what the entity browser and the
   * entity detail view want: the whole job's metric catalogue and label
   * universe.
   */
  product?: string
}

interface UseEntityFilterSummaryResult {
  /** Entity label keys → their distinct values with entity counts. */
  labels: Record<string, LabelValue[]> | undefined
  /**
   * Distinct platforms with entity counts — the UNFILTERED universe, which is
   * what decides WHICH toggles exist. The counts shown ON those toggles come
   * from `useSkyPlatformCounts` instead, because those must move with the
   * page's filters and this deliberately does not.
   */
  platforms: LabelValue[]
  /**
   * Distinct Sprinklr campaigns with entity counts. `value` is the id that
   * filters, `label` the display name to show. Includes the `none` / `unknown`
   * sentinels — one of them is the running count of videos we could not key.
   */
  campaigns: LabelValue[]
  /** Metric slider bounds (real MIN/MAX) with display metadata. */
  metrics: EntityMetricBounds[]
  /** Published-date extent across the job. */
  publishedRange: EntityFilterSummary['publishedRange']
  /** Total entities (videos/posts) in the job — replaces the pipeline count. */
  totalEntities: number
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Hook for the entity filter summary, on whichever mount applies.
 *
 * The endpoint is deliberately **unfiltered by the page's own filters** — it
 * describes the universe so the sidebar's checkbox lists and slider bounds stay
 * stable while the user filters. It is therefore NOT keyed on `labelsKey`.
 *
 * ⚠ "The universe" is the MOUNT's, not always the job's: with `product` it is
 * that product's campaigns (server-side, T1.1), without it the whole job.
 */
export function useEntityFilterSummary(
  options: UseEntityFilterSummaryOptions,
): UseEntityFilterSummaryResult {
  const { customer, job, enabled = true, product } = options
  const account = useAccount()

  const url = product
    ? skyProductURL(product, '/entities/filter-summary')
    : `${API_BASE_URL}/autocoder/${customer}/${job}/entities/filter-summary`

  // Keyed on the product too, so switching departments is a cache MISS rather
  // than a flash of the previous department's facets — the same rule
  // skyQueryKey exists to enforce for every other socialdash hook.
  const queryKey = product
    ? skyQueryKey(product, 'entity_filter_summary')
    : (['entity_filter_summary', { customer, job }] as const)

  const query = useQuery({
    queryKey,
    // With a product the scope is server-side, so customer/job are not needed
    // to make the request well-formed — the route group fixes both.
    enabled: Boolean((product || (customer && job)) && account.user && enabled),
    staleTime: 30000,
    queryFn: async (): Promise<EntityFilterSummary | null> => {
      if (!account.user) return null
      if (!product && (!customer || !job)) return null
      try {
        const res = await axios.get<EntityFilterSummary>(url)
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  return {
    labels: query.data?.labels,
    platforms: query.data?.platforms ?? [],
    campaigns: query.data?.campaigns ?? [],
    metrics: query.data?.metrics ?? [],
    publishedRange: query.data?.publishedRange,
    totalEntities: query.data?.totalEntities ?? 0,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: () => query.refetch(),
  }
}

export default useEntityFilterSummary
